import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MagnifyingGlassIcon, BookOpenIcon, SparklesIcon, PaperAirplaneIcon, XMarkIcon } from '@heroicons/react/24/outline';
import apiClient from '../services/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';

interface SearchResult {
  term: string;
  short_definition: string;
  category: string;
}

interface TermExplanation {
  term: string;
  short_definition: string;
  ai_explanation: string;
  category: string;
  related_terms: string[];
  in_dictionary: boolean;
}

interface CategorySuggestion {
  term: string;
  suggested_category: string;
  available_categories: string[];
  confidence: string;
  explanation: string;
}

export const MedicalGlossary: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [selectedTerm, setSelectedTerm] = useState<TermExplanation | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingExplanation, setIsLoadingExplanation] = useState(false);
  const [simpleMode, setSimpleMode] = useState(false);
  const [categories, setCategories] = useState<Record<string, SearchResult[]>>({});
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showCategories, setShowCategories] = useState(true);
  const [isAddingTerm, setIsAddingTerm] = useState(false);
  const [addTermMessage, setAddTermMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categorySuggestion, setCategorySuggestion] = useState<CategorySuggestion | null>(null);
  const [selectedAddCategory, setSelectedAddCategory] = useState<string>('');
  const [isLoadingCategory, setIsLoadingCategory] = useState(false);
  const [initialQueryProcessed, setInitialQueryProcessed] = useState(false);

  // Load categories on mount
  useEffect(() => {
    loadCategories();
  }, [i18n.language]);

  // Handle initial query from navigation
  useEffect(() => {
    const state = location.state as { initialQuery?: string } | null;
    if (state?.initialQuery && !initialQueryProcessed) {
      // Extract the term from queries like "What is an MRI?"
      let term = state.initialQuery;
      const whatIsMatch = term.match(/what\s+is\s+(?:a|an|the)?\s*(.+?)[\?]?$/i);
      if (whatIsMatch) {
        term = whatIsMatch[1].trim();
      }
      setSearchQuery(term);
      setInitialQueryProcessed(true);
      // Trigger search after a short delay
      setTimeout(() => {
        handleSearch(term);
      }, 100);
    }
  }, [location.state, initialQueryProcessed]);

  const loadCategories = async () => {
    try {
      const response = await apiClient.get('/medical-glossary/categories', {
        params: { language: i18n.language }
      });
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setShowCategories(false);
    setAddTermMessage(null); // Clear any previous add term messages

    try {
      // First, search the dictionary for matches
      const response = await apiClient.get('/medical-glossary/search', {
        params: { query, language: i18n.language }
      });

      setSearchResults(response.data.results);

      // If no dictionary matches found, get AI explanation for the term
      if (response.data.results.length === 0) {
        await handleTermClick(query.trim());
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleTermClick = async (term: string) => {
    setIsLoadingExplanation(true);
    setAddTermMessage(null); // Clear any previous add term messages
    try {
      const response = await apiClient.post('/medical-glossary/explain', {
        term,
        simple_explanation: simpleMode,
        language: i18n.language
      }, {
        headers: {
          'Accept-Language': i18n.language
        }
      });
      setSelectedTerm(response.data);
    } catch (error) {
      console.error('Failed to get explanation:', error);
    } finally {
      setIsLoadingExplanation(false);
    }
  };

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
    setShowCategories(false);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedTerm(null);
    setAddTermMessage(null); // Clear any previous add term messages
  };

  const handleBack = () => {
    setShowCategories(true);
    setSelectedCategory(null);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedTerm(null);
    setAddTermMessage(null); // Clear any previous add term messages
  };

  const handleAddToDictionary = async () => {
    if (!selectedTerm) return;

    setIsLoadingCategory(true);
    setAddTermMessage(null);

    try {
      // First, get category suggestion from LLM
      const response = await apiClient.post('/medical-glossary/suggest-category', {
        term: selectedTerm.term,
        short_definition: selectedTerm.short_definition,
        language: i18n.language
      });

      setCategorySuggestion(response.data);
      setSelectedAddCategory(response.data.suggested_category);
      setShowCategoryModal(true);
    } catch (error: any) {
      console.error('Failed to get category suggestion:', error);
      // Fallback: show modal with available categories from current categories
      const availableCategories = Object.keys(categories);
      if (!availableCategories.includes('General')) {
        availableCategories.push('General');
      }
      setCategorySuggestion({
        term: selectedTerm.term,
        suggested_category: 'General',
        available_categories: availableCategories.sort(),
        confidence: 'low',
        explanation: t('glossary:categoryModal.fallbackExplanation', 'Unable to determine best category')
      });
      setSelectedAddCategory('General');
      setShowCategoryModal(true);
    } finally {
      setIsLoadingCategory(false);
    }
  };

  const handleConfirmAddTerm = async () => {
    if (!selectedTerm || !selectedAddCategory) return;

    setIsAddingTerm(true);
    setAddTermMessage(null);

    try {
      const response = await apiClient.post('/medical-glossary/add-term', {
        term: selectedTerm.term,
        short_definition: selectedTerm.short_definition,
        category: selectedAddCategory,
        language: i18n.language
      });

      if (response.data.success) {
        setAddTermMessage({
          type: 'success',
          text: response.data.message
        });

        // Update the selected term to show it's now in the dictionary
        setSelectedTerm({
          ...selectedTerm,
          in_dictionary: true,
          category: selectedAddCategory
        });

        // Reload categories to include the new term
        await loadCategories();

        // Close the modal
        setShowCategoryModal(false);
        setCategorySuggestion(null);
      } else {
        setAddTermMessage({
          type: 'error',
          text: response.data.message
        });
      }
    } catch (error: any) {
      console.error('Failed to add term to dictionary:', error);
      setAddTermMessage({
        type: 'error',
        text: error.response?.data?.detail || t('glossary:errors.addTermFailed', 'Failed to add term to dictionary')
      });
    } finally {
      setIsAddingTerm(false);
    }
  };

  const handleCancelAddTerm = () => {
    setShowCategoryModal(false);
    setCategorySuggestion(null);
    setSelectedAddCategory('');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <img
            src="/caduceus.png"
            alt="Caduceus"
            className="h-24 w-24 mx-auto mb-4"
          />
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {t('glossary:title', 'Medical Glossary')}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {t('glossary:subtitle', 'Search medical terms and get AI-powered explanations')}
          </p>
        </div>

        {/* Search Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (!e.target.value) {
                    setSearchResults([]);
                    setShowCategories(true);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchQuery.trim()) {
                    e.preventDefault();
                    handleSearch(searchQuery);
                  }
                }}
                placeholder={t('glossary:searchPlaceholder', 'Search for a medical term...')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => {
                if (searchQuery.trim()) {
                  handleSearch(searchQuery);
                }
              }}
              disabled={!searchQuery.trim()}
              className="p-2 text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Search"
            >
              <PaperAirplaneIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Categories or Search Results */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              {showCategories && !searchQuery ? (
                <>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {t('glossary:categories', 'Categories')}
                  </h2>
                  <div className="space-y-2">
                    {Object.keys(categories).map((category) => (
                      <button
                        key={category}
                        onClick={() => handleCategoryClick(category)}
                        className={`w-full text-left px-4 py-2 rounded-lg ${
                          selectedCategory === category
                            ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                        } transition-colors`}
                      >
                        {category} ({categories[category].length})
                      </button>
                    ))}
                  </div>
                </>
              ) : searchResults.length > 0 ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {t('glossary:searchResults', 'Search Results')} ({searchResults.length})
                    </h2>
                    <button
                      onClick={handleBack}
                      className="text-sm text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300"
                    >
                      {t('glossary:back', 'Back')}
                    </button>
                  </div>
                  <div className="space-y-2">
                    {searchResults.map((result) => (
                      <button
                        key={result.term}
                        onClick={() => handleTermClick(result.term)}
                        className={`w-full text-left px-4 py-3 rounded-lg border ${
                          selectedTerm?.term === result.term
                            ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/30'
                            : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                        } transition-colors`}
                      >
                        <div className="font-medium text-gray-900 dark:text-white">{result.term}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{result.short_definition}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">{result.category}</div>
                      </button>
                    ))}
                  </div>
                </>
              ) : selectedCategory && categories[selectedCategory] ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{selectedCategory}</h2>
                    <button
                      onClick={handleBack}
                      className="text-sm text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300"
                    >
                      {t('glossary:back', 'Back')}
                    </button>
                  </div>
                  <div className="space-y-2">
                    {categories[selectedCategory].map((result) => (
                      <button
                        key={result.term}
                        onClick={() => handleTermClick(result.term)}
                        className={`w-full text-left px-4 py-3 rounded-lg border ${
                          selectedTerm?.term === result.term
                            ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/30'
                            : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                        } transition-colors`}
                      >
                        <div className="font-medium text-gray-900 dark:text-white">{result.term}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{result.short_definition}</div>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  {isSearching
                    ? t('glossary:searching', 'Searching...')
                    : t('glossary:noResults', 'No results found')}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Term Explanation */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              {isLoadingExplanation ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 dark:border-emerald-400 mx-auto"></div>
                  <p className="mt-4 text-gray-600 dark:text-gray-400">{t('glossary:loading', 'Loading explanation...')}</p>
                </div>
              ) : selectedTerm ? (
                <>
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{selectedTerm.term}</h2>
                      {!selectedTerm.in_dictionary && (
                        <button
                          onClick={handleAddToDictionary}
                          disabled={isAddingTerm || isLoadingCategory}
                          className="px-4 py-2 bg-emerald-600 dark:bg-emerald-700 hover:bg-emerald-700 dark:hover:bg-emerald-600 text-white rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isLoadingCategory
                            ? t('glossary:categoryModal.analyzing', 'Analyzing...')
                            : isAddingTerm
                              ? t('glossary:adding', 'Adding...')
                              : t('glossary:addToDictionary', 'Add to Dictionary')}
                        </button>
                      )}
                    </div>
                    <span className="inline-block px-3 py-1 text-sm bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 rounded-full">
                      {selectedTerm.category}
                    </span>
                  </div>

                  {/* Add term message */}
                  {addTermMessage && (
                    <div className={`mb-6 p-4 rounded-lg ${
                      addTermMessage.type === 'success'
                        ? 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                        : 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                    }`}>
                      {addTermMessage.text}
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      {t('glossary:definition', 'Definition')}
                    </h3>
                    <p className="text-gray-900 dark:text-white">{selectedTerm.short_definition}</p>
                  </div>

                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {simpleMode
                          ? t('glossary:simpleExplanation', 'Simple Explanation')
                          : t('glossary:detailedExplanation', 'Detailed Explanation')}
                      </h3>
                      <button
                        onClick={() => {
                          setSimpleMode(!simpleMode);
                          handleTermClick(selectedTerm.term);
                        }}
                        className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg border text-sm ${
                          simpleMode
                            ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                        } hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors`}
                      >
                        <SparklesIcon className="h-4 w-4" />
                        <span>{t('glossary:simpleMode', 'Simple explanation')}</span>
                      </button>
                    </div>
                    <div className="prose prose-sm dark:prose-invert max-w-none text-gray-900 dark:text-white">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {selectedTerm.ai_explanation}
                      </ReactMarkdown>
                    </div>
                  </div>

                  {selectedTerm.related_terms.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        {t('glossary:relatedTerms', 'Related Terms')}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedTerm.related_terms.map((term) => (
                          <button
                            key={term}
                            onClick={() => handleTermClick(term)}
                            className="px-3 py-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm transition-colors"
                          >
                            {term}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <BookOpenIcon className="h-16 w-16 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
                  <p>{t('glossary:selectTerm', 'Select a term to view its explanation')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Category Selection Modal */}
      {showCategoryModal && categorySuggestion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('glossary:categoryModal.title', 'Select Category')}
              </h3>
              <button
                onClick={handleCancelAddTerm}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {t('glossary:categoryModal.description', 'Choose a category for this term:')}
              </p>

              {/* Term Info */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 mb-4">
                <p className="font-medium text-gray-900 dark:text-white">{categorySuggestion.term}</p>
              </div>

              {/* AI Suggestion */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('glossary:categoryModal.aiSuggestion', 'AI Suggested Category')}
                </label>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    categorySuggestion.confidence === 'high'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                      : categorySuggestion.confidence === 'medium'
                        ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                  }`}>
                    {categorySuggestion.suggested_category}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    ({categorySuggestion.confidence} {t('glossary:categoryModal.confidence', 'confidence')})
                  </span>
                </div>
                {categorySuggestion.explanation && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {categorySuggestion.explanation}
                  </p>
                )}
              </div>

              {/* Category Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('glossary:categoryModal.selectCategory', 'Category')}
                </label>
                <select
                  value={selectedAddCategory}
                  onChange={(e) => setSelectedAddCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  {categorySuggestion.available_categories.map((category) => (
                    <option key={category} value={category}>
                      {category} {category === categorySuggestion.suggested_category ? `(${t('glossary:categoryModal.recommended', 'recommended')})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Add term error message in modal */}
              {addTermMessage && addTermMessage.type === 'error' && (
                <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 text-sm">
                  {addTermMessage.text}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <button
                onClick={handleCancelAddTerm}
                disabled={isAddingTerm}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                {t('glossary:categoryModal.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleConfirmAddTerm}
                disabled={isAddingTerm || !selectedAddCategory}
                className="px-4 py-2 bg-emerald-600 dark:bg-emerald-700 hover:bg-emerald-700 dark:hover:bg-emerald-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAddingTerm
                  ? t('glossary:adding', 'Adding...')
                  : t('glossary:categoryModal.confirm', 'Add to Dictionary')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
